import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Phase 2Z.aa — intercept Command-only 402 responses globally so paid
// Starter / Growth users see a friendly upgrade nudge instead of a
// raw error. The backend gates AI Assistant + Autopilot endpoints via
// _require_command_feature and returns:
//   { code: "FEATURE_REQUIRES_COMMAND", feature: "AI Assistant" | "Autopilot", ... }
// Trial / founder users bypass the gate server-side, so they never
// reach this branch.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Feb 2026 -- production email verification gate. Backend
      // returns 403 { code: "email_verification_required" } for any
      // protected path while the flag is on and the user is not yet
      // verified. Route the browser to the verify-needed screen so
      // the customer can resend the link or continue verification.
      if (status === 403
          && detail
          && typeof detail === "object"
          && detail.code === "email_verification_required") {
        const path = (typeof window !== "undefined" && window.location)
          ? window.location.pathname : "";
        // Only redirect from real app pages; leave the verify pages
        // and public routes alone so the resend / verify flows work.
        const skip = path.startsWith("/verify-email")
          || path.startsWith("/login")
          || path.startsWith("/register")
          || path.startsWith("/start");
        if (!skip && typeof window !== "undefined") {
          window.location.assign("/verify-email-needed");
        }
      }
      if (status === 402 && detail && detail.code === "FEATURE_REQUIRES_COMMAND") {
        const feature = detail.feature || "This feature";
        const isAI = /AI/i.test(feature);
        toast.error(
          isAI ? "AI Assistant is a Command feature" : "Autopilot is a Command feature",
          {
            description: isAI
              ? "Upgrade to Command to use AI-assisted rota intelligence. You can try it during your full Command trial or founder access."
              : "Command unlocks manager-controlled automation: automatic drafts, approval rules, publishing rules and coverage-aware leave approval.",
            duration: 8000,
          },
        );
      }
    } catch { /* never let interceptor itself throw */ }
    return Promise.reject(err);
  },
);

export function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return friendlyMessage(detail);
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? friendlyMessage(e.msg) : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return friendlyMessage(detail.msg);
  // Phase 2Z.ae - some backend HTTPException payloads use `message`
  // (e.g. /drafts/rota/run-from-style returning a structured 409 for
  // already-published weeks). Without this branch the UI rendered the
  // whole object via String(detail) -> "[object Object]".
  if (detail && typeof detail.message === "string") return friendlyMessage(detail.message);
  return "Something went wrong. Please try again.";
}

/**
 * Translate technical backend error strings into user-friendly copy.
 * Add new mappings here whenever the backend introduces another short message.
 */
function friendlyMessage(raw) {
  if (!raw) return raw;
  const msg = String(raw);
  if (/Out of (team )?scope/i.test(msg)) {
    return "You don't have permission to edit this row. The employee may belong to a different team or be inactive.";
  }
  if (/^Forbidden$/i.test(msg)) {
    return "You don't have permission for this action.";
  }
  // TP-RS.team.2 -- Workspace Admin wording. Backend `_company_admin_only`
  // now returns "This action is restricted to the Workspace Admin." but
  // the older "Simulation API is currently company-admin only" string is
  // still mapped here in case any tenant has cached responses.
  if (/Simulation API is currently company-admin only/i.test(msg) ||
      /restricted to the Workspace Admin/i.test(msg)) {
    return "This action is restricted to the Workspace Admin.";
  }
  if (/is locked.*Reopen|locked\. Reopen/i.test(msg)) {
    return "This week is locked. Reopen it from Roster → Actions → Reopen, or use lock override (Workspace Admin only).";
  }
  if (/Leave categories cannot be bulk-applied/i.test(msg)) {
    return "Leave categories can't be bulk-applied. Use the Leave page to record approved time off.";
  }
  if (/Half-day leave must be a single date/i.test(msg)) {
    return "Half-day leave must cover a single date - please split the request.";
  }
  if (/end_date before start_date|end time must be after start/i.test(msg)) {
    return "End time/date must be after the start.";
  }
  // Phase 2Z.ae - friendly copy for the structured 409 the Rota Style
  // "Generate draft" button hits when the target week already has live
  // shifts published. The backend safety logic (server.py L15319) is
  // unchanged - we only make the message readable.
  if (/already has a live rota/i.test(msg)) {
    return "This week already has a published rota. A live rota already exists for this week. To avoid overwriting published shifts, edit this week from the Roster page or choose another week.";
  }
  return msg;
}
